import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import { classArr } from "../helper/style-helper";
import * as styles from "./velavu-input.module.scss";

type VelavuInputVariant = "normal" | "circular";

interface ReactProps {
	className?: string;
	value?: string;
	titleText?: string | React.ReactNode;
	placeholder?: string;
	helperText?: string;
	errorText?: string;
	autoComplete?: string;
	spellCheck?: boolean;
	noAutofill?: boolean;

	italicPlaceholder?: boolean;
	fullWidth?: boolean;
	autoFocus?: boolean;
	variant?: VelavuInputVariant;
	disabled?: boolean;
	error?: boolean;
	bottomPadding?: boolean;
	inputClassName?: string;

	maxLength?: number;

	style?: CSSProperties;

	leadingIcon?: React.ReactNode;
	trailingIcon?: React.ReactNode;

	onChange?: (value: string) => void;
	onFocus?: () => void;
	onBlur?: () => void;
}

interface InputProps extends ReactProps {
	inputProps?: React.DetailedHTMLProps<
		React.InputHTMLAttributes<HTMLInputElement>,
		HTMLInputElement
	>;
	multiline?: false | undefined;
	inputRef?: React.Ref<HTMLInputElement>;
	type?: "text" | "password" | "search" | "tel" | "email";
	autoResize?: never;
}

interface TextareaProps extends ReactProps {
	inputProps?: React.DetailedHTMLProps<
		React.TextareaHTMLAttributes<HTMLTextAreaElement>,
		HTMLTextAreaElement
	>;
	multiline: true;
	inputRef?: React.Ref<HTMLTextAreaElement>;
	rows?: number;
	autoResize?: boolean;
}

//A reusable flexible input field that conforms to Velavu's design language
export default function VelavuInput(props: InputProps | TextareaProps) {
	const [isFocused, setFocused] = useState(false);

	const {
		disabled: propsDisabled,
		onFocus: propsOnFocus,
		onBlur: propsOnBlur,
		inputRef: propsInputRef,
		onChange: propsOnChange,
	} = props;

	const enableFocus = useCallback(() => {
		if (!propsDisabled) setFocused(true);
		if (propsOnFocus) propsOnFocus();
	}, [propsDisabled, setFocused, propsOnFocus]);
	const disableFocus = useCallback(() => {
		setFocused(false);
		if (propsOnBlur) propsOnBlur();
	}, [setFocused, propsOnBlur]);

	useEffect(() => {
		//If we're disabled, disable focus
		if (props.disabled) setFocused(false);
	}, [props.disabled, setFocused]);

	//Keep track of the input ref internally
	const [inputRef, setInputRef] = useState<
		HTMLInputElement | HTMLTextAreaElement | null
	>(null);
	const updateInputRef = useCallback(
		(element: HTMLInputElement | HTMLTextAreaElement | null) => {
			setInputRef(element);
			if (propsInputRef) {
				if (typeof propsInputRef === "function") {
					//@ts-ignore
					propsInputRef(element);
				} else {
					propsInputRef.current = element;
				}
			}
		},
		[setInputRef, propsInputRef],
	);

	//Update the size on mount
	useEffect(() => {
		if (props.autoResize && inputRef !== null) {
			updateAutoResize(inputRef);
		}
	}, [inputRef, props.autoResize]);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			propsOnChange?.(event.target.value);

			if (props.autoResize) {
				updateAutoResize(event.target);
			}
		},
		[propsOnChange, props.autoResize],
	);

	return (
		<div
			style={props.style}
			className={classArr(
				styles.container,
				props.className,
				props.bottomPadding && styles.bottomPadding,
			)}
		>
			{props.titleText && (
				<span className={styles.titleText}>{props.titleText}</span>
			)}

			<div
				className={classArr(
					styles.content,
					props.leadingIcon
						? styles.contentLeadingIcon
						: styles.contentLeadingText,
					props.trailingIcon
						? styles.contentTrailingIcon
						: styles.contentTrailingText,
					isFocused && styles.focus,
					props.variant === "circular"
						? styles.containerCircular
						: styles.containerRound,
					props.fullWidth && styles.fullWidth,
					props.error ? styles.borderError : styles.borderNormal,
					props.disabled ? styles.inputDisabled : styles.inputEnabled,
				)}
				onFocus={enableFocus}
				onBlur={disableFocus}
			>
				{props.leadingIcon && (
					<div className={styles.leadingIcon}>
						{props.leadingIcon}
					</div>
				)}
				{props.multiline ? (
					<textarea
						ref={updateInputRef}
						{...props.inputProps}
						className={classArr(
							styles.editable,
							props.italicPlaceholder && styles.italicPlaceholder,
							props.inputClassName,
						)}
						value={props.value}
						onChange={onChange}
						data-lpignore={props.noAutofill ? "true" : undefined}
						autoFocus={props.autoFocus}
						placeholder={props.placeholder}
						rows={props.rows}
						autoComplete={props.autoComplete}
						spellCheck={props.spellCheck ?? true}
						disabled={props.disabled}
					/>
				) : (
					<input
						ref={updateInputRef}
						{...props.inputProps}
						className={classArr(
							styles.editable,
							props.italicPlaceholder && styles.italicPlaceholder,
							props.inputClassName,
						)}
						value={props.value}
						onChange={onChange}
						data-lpignore={props.noAutofill ? "true" : undefined}
						autoFocus={props.autoFocus}
						placeholder={props.placeholder}
						type={props.type}
						autoComplete={props.autoComplete}
						spellCheck={props.spellCheck ?? true}
						disabled={props.disabled}
						maxLength={props.maxLength}
					/>
				)}
				{props.trailingIcon && (
					<div className={styles.trailingIcon}>
						{props.trailingIcon}
					</div>
				)}
			</div>

			{(!props.error || !props.errorText) && props.helperText && (
				<span className={`${styles.bottomText} ${styles.helperText}`}>
					{props.helperText}
				</span>
			)}
			{props.error && props.errorText && (
				<span className={`${styles.bottomText} ${styles.errorText}`}>
					{props.errorText}
				</span>
			)}
		</div>
	);
}

function updateAutoResize(target: HTMLInputElement | HTMLTextAreaElement) {
	//Resolve the padding to calculate the height correctly
	const style = getComputedStyle(target, null);
	const paddingTop = parseFloat(style.getPropertyValue("padding-top"));
	const paddingBottom = parseFloat(style.getPropertyValue("padding-bottom"));

	//Reset the height to auto to shrink if needed
	target.style.height = "auto";

	//Calculate the height based on the scrollHeight minus padding
	const height = target.scrollHeight - paddingTop - paddingBottom;
	target.style.height = height + "px";
}
